import React, { useEffect, useState, useCallback} from 'react';

import SoftCard from '../../SoftCard';

import Users from '../../../../api/Users';

import styles from './GroupDisplays.module.scss'

/**
 * Takes in a user id to display the families/groups a user is a part of. 
 * 
 * @param {int} userId - the user to look up/get details for 
 * @param {boolean} showTitle - if true, will show the title "groups", "family", or both as needed
 * @param {boolean} border - if there's a border around the whole section
 * @param {string} maxWidth - the max width of the container
 * @param {string} cardMaxWidth - the max width of the card format
 * @param {boolean} showFamily - if true, will show the family groups
 * @param {boolean} showOther - if true, will show the other groups(aside from family)
 * @param {boolean} showCard - if true, will show the group in a card format instead of a list
 * @param {boolean} showRole - if true, will show the role of the user in the group
 * @param {function} onClick - will pass the clicked on group (the entire object) to the parent, otherwise will just return
 */
export const GroupDisplays = ({
    userId,
    importedGroups=null,
    title="GROUPS",
    subtitle=null,
    border=true,
    maxWidth="250px",
    cardWidth="150px",
    showFamily=true,
    showOther=true,
    showCard=false,
    showRole=true,
    onClick,
    alignment="center",
    hover=false,
    loadCallback=null
}) => {

    const iconOptions = [
        {name: "Department", icon: "fad fa-user-tie"},
        {name: "Team", icon: "fad fa-users-crown"},
        {name: "Friends", icon: "fad fa-user-friends"},
        {name: "Family", icon: "fad fa-users"},
        {name: "League", icon: "fad fa-users-class"},
        {name: "Club", icon: "fad fa-user-headset"},
        {name: "Class", icon: "fad fa-chalkboard-teacher"},
        {name: "Employees", icon: "fad fa-user-hard-hat"}
    ]

    const [ error, setError ] = useState(null);
    const [ groups, setGroups ] = useState();

    const filterGroups = useCallback((groups)=>{
        let filtered = []
        if(showFamily && showOther) filtered = groups;
        else if(showFamily && !showOther) filtered = groups?.filter((group)=>group.group_type_name==="Family")
        else if(!showFamily && showOther) filtered = groups?.filter((group)=>group.group_type_name!=="Family")
        return filtered;
    },[showFamily, showOther])

    const getGroups =useCallback(async()=>{
        try{
            let response = await Users.groups({id: userId});
            let filtered = filterGroups(response.data)
            setGroups(filtered)
            if(loadCallback) loadCallback(filtered);
        }catch(ex){
            console.error(ex)
            setError(ex)
            if(loadCallback) loadCallback(null);
        }
    },[userId, filterGroups, loadCallback])

    useEffect(()=>{
        if(userId && !importedGroups) getGroups();
        else if(importedGroups) {
            let filtered = filterGroups(importedGroups)
            setGroups(filtered);
        }
    },[userId, getGroups, importedGroups, filterGroups]);

    const handleEachClick=(group)=>{
        if(onClick) onClick(group);
        else return;
    }
    

    return(
        <div className={`
            ${styles["group-displays-wrapper"]}
            ${border ? styles["border"] : ""}
        `}
            style={{maxWidth: maxWidth}}
        >
            {title &&
                <p className={styles["title"]}>{title}</p>
            }
            {subtitle &&
                <p className={styles["subtitle"]}>{subtitle}</p>
            }
            <div className={styles["main-body"]}>
                {error && 
                    <p className="error-text">There was an error loading the groups. Please try again later.</p>
                }
                {showCard && !error ?
                    <>
                        {groups?.map((group)=>(
                            <SoftCard 
                                key={`group-card-${group.group_id}`} 
                                onClick={()=>handleEachClick(group)}
                                width={cardWidth}
                                hover={hover}
                                border={border}
                            >
                                <SoftCard.Header>
                                    {group?.icon ? 
                                        <img src={group.icon} alt={`${group.group_type_name} icon`} className={styles["group-icon"]} />
                                        :
                                        <i className={`${styles["group-icon"]} ${iconOptions?.filter((option)=>option.name===group.group_type_name)[0]?.icon}`} />
                                    }
                                    {" "}
                                    {group?.name}
                                </SoftCard.Header>
                                <p>
                                    Type: {group?.group_type_name}
                                </p>
                                <p>
                                    Role: {group?.group_member_role}
                                </p>
                            </SoftCard>
                        ))}
                    </>
                :
                    <>
                        {groups?.map((group)=>(
                            <div 
                                key={`group-${group.group_id}`}
                                className={`
                                    ${styles["each-group-list"]}
                                    ${onClick ? styles["clickable"] : ""}
                                    ${hover ? styles["hover"] : ""}
                                    ${alignment==="center" ? styles["center"] : ""}
                                `}
                                onClick={()=>handleEachClick(group)}
                            >
                                {group?.icon ? 
                                    <img src={group.icon} alt={`${group.group_type_name} icon`} className={styles["group-icon"]} />
                                    :
                                    <i className={`${styles["group-icon"]} ${iconOptions?.filter((option)=>option.name===group.group_type_name)[0]?.icon}`} />
                                }
                                {" "}
                                {showFamily && showOther && showRole &&
                                    <span className={styles["group-list-name"]}>
                                        ({group?.group_type_name} {showRole ? `- ${group?.group_member_role}` : ""}) {" "}
                                    </span>
                                }
                                {!showFamily && !showOther && showRole &&
                                    <span className={styles["group-list-name"]}>
                                        ({group?.group_member_role}){" "}
                                    </span>
                                }
                                {group?.name}
                            </div>
                        ))}
                    </>
                }
            </div>

        </div>
    )

}